import { Flex } from '@chakra-ui/react';
import { FormFeedback, Input } from '@morressier/alembic';
import { useState } from 'react';
import {
  get,
  FieldError as IFieldError,
  useFormContext
} from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { IFormFieldText } from 'src/components/Submission/GenericFieldComponents/Text';
import {
  maxPasswordLimit,
  toTitleCase
} from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import PasswordVisibilityToggle from './PasswordVisibilityToggle';

export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+=\-[\]{};:'"\\|,.<>/?~`]{8,}$/;
  
const CustomFormFieldPasswordInput = ({
  config,
  dependentField
}: {
  config: IFormFieldText;
  dependentField?: string;
}) => {
  const {
    register,
    formState: { errors },
    watch,
    trigger,
    getValues
  } = useFormContext();

  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const error = get(errors, field) as IFieldError;
  const value = watch(field) as string;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  return (
    <Flex direction={'column'} gap={2} flex={1}>
      <FieldLabel
        label={labels.main}
        subText={labels.subtitle}
        isRequired={validations.required}
        helpText={labels.helpText}
      />
      <Input
        id={field}
        aria-label={field}
        type={isPasswordVisible ? 'text' : 'password'}
        iconRight={
          <PasswordVisibilityToggle
            isVisible={isPasswordVisible}
            onToggle={() => setIsPasswordVisible(state => !state)}
            ariaLabel={`Toggle visibility for ${labels.main}`}
          />
        }
        placeholder={labels.placeholder}
        value={value ?? ''}
        {...register(field, {
          required: toTitleCase(
            `Please ${
              field.includes('confirmPassword')
                ? 'confirm your password'
                : `set a ${labels.main}`
            }`
          ),
          maxLength: {
            value: maxPasswordLimit,
            message: `${labels.main} cannot exceed ${maxPasswordLimit} characters.`
          },
          validate: value => {
            if (value.trim().length === 0) {
              return `${labels.main} cannot be just whitespace`;
            }
            // 2. No leading or trailing spaces
            if (/^\s|\s$/.test(value)) {
              return 'No leading or trailing spaces allowed';
            }
            if (!passwordRegex.test(value)) {
              return `${toTitleCase(
                labels.main
              )} must be at least 8 characters and include at least one uppercase letter, one lowercase letter and one number`;
            }

            // Match check
            if (dependentField && value !== getValues(dependentField)) {
              return 'This password does not match the password set above. Please ensure both passwords match';
            }

            return true;
          }
        })}
        onBlur={() => trigger(field)}
        isInvalid={!!error}
        data-field-error={!!error}
        autoComplete="new-password"
      />
      {error?.message && (
        <FormFeedback variant="error" data-error={`${field}-error`}>
          {error?.message}
        </FormFeedback>
      )}
    </Flex>
  );
};

export default CustomFormFieldPasswordInput;
